import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { label: "About Us", href: "/about" },
  { label: "Our Services", href: "/services" },
  { label: "Our Products", href: "/products" },
  { label: "Contact Us", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showScrolled = scrolled || !isHome || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolled
          ? "bg-cream-50/80 backdrop-blur-md border-b border-ink-950/5"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-6 md:px-10 lg:px-14">
        <div className="flex h-16 md:h-20 items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2.5 cursor-pointer group"
          >
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-ink-950">
              <span className="font-display font-bold text-cream-50 text-sm leading-none">I</span>
            </div>
            <span
              className={`font-display font-bold text-xl tracking-tight transition-colors duration-300 ${
                showScrolled ? "text-ink-950" : "text-cream-50"
              }`}
            >
              Trevica&nbsp;
              <span
                className={`font-sans font-light text-sm tracking-widest transition-colors duration-300 ${
                  showScrolled ? "text-ink-500" : "text-cream-50/60"
                }`}
              >
                LTD
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-9">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? showScrolled
                        ? "text-ink-950 font-semibold"
                        : "text-cream-50 font-semibold"
                      : showScrolled
                        ? "text-ink-700 hover:text-ink-950"
                        : "text-cream-50/80 hover:text-cream-50"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-cream-50 bg-amber-500 hover:bg-amber-600 px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              Get in touch
              <i className="ri-arrow-right-up-line text-base"></i>
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              showScrolled
                ? "text-ink-950 hover:bg-ink-950/5"
                : "text-cream-50 hover:bg-cream-50/10"
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden pb-4 pt-2 border-t border-ink-950/5 bg-cream-50/80 backdrop-blur-md">
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={`px-2 py-2.5 text-sm font-medium cursor-pointer ${
                      isActive
                        ? "text-ink-950 font-semibold"
                        : "text-ink-700 hover:text-ink-950"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-2 mx-[10px] inline-flex items-center justify-center gap-1.5 text-sm font-medium text-cream-50 bg-amber-500 px-4 py-2.5 rounded-full cursor-pointer whitespace-nowrap"
              >
                Get in touch
                <i className="ri-arrow-right-up-line text-base"></i>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}